import { supabase } from '@/lib/supabase';
import { ApiError, ApiException } from '@/utils/errorHandler';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://www.getpoints.com.br';

async function getToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

async function apiFetch<T>(method: string, endpoint: string, body?: unknown): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    const json = await res.json().catch(() => null);

    if (!res.ok) {
      const errorPayload: ApiError = {
        timestamp: json?.timestamp || new Date().toISOString(),
        status: json?.status || res.status,
        error: json?.error || 'Unexpected Error',
        message: json?.message || 'Erro inesperado',
        path: json?.path || endpoint,
        traceId: json?.traceId || 'unknown',
        details: json?.details || [],
      };
      throw new ApiException(errorPayload);
    }

    return json as T;
  } catch (err) {
    // Re-throw ApiException as-is
    if (err instanceof ApiException) {
      throw err;
    }

    // Convert network errors to ApiException
    if (err instanceof Error) {
      const errorPayload: ApiError = {
        timestamp: new Date().toISOString(),
        status: 0,
        error: 'Network Error',
        message: err.message === 'Failed to fetch' || err.message === 'NetworkError when attempting to fetch resource.'
          ? 'Servidor indisponível no momento. Verifique sua conexão e tente novamente.'
          : err.message,
        path: endpoint,
        traceId: 'unknown',
        details: ['Erro de conexão. Verifique se o servidor está online ou se há problemas de rede.'],
      };
      throw new ApiException(errorPayload);
    }

    throw err;
  }
}

// ─── Idioma ───
export interface IdiomaResponse {
  idioma: string;
  idiomasDisponiveis: Array<{ codigo: string; nome: string }>;
}

export async function getLanguage(): Promise<IdiomaResponse> {
  return apiFetch<IdiomaResponse>('GET', '/api/v1/account/settings/languages');
}

export async function updateLanguage(idioma: string): Promise<IdiomaResponse> {
  return apiFetch<IdiomaResponse>('PUT', '/api/v1/account/settings/languages', { idioma });
}

// ─── Acessibilidade ───
export interface AcessibilidadeResponse {
  tamanhoFonte: 'normal' | 'large' | 'xlarge';
  cursorMaior: boolean;
}

export async function getAccessibility(): Promise<AcessibilidadeResponse> {
  return apiFetch<AcessibilidadeResponse>('GET', '/api/v1/account/settings/accessibility');
}

export async function updateAccessibility(data: Partial<AcessibilidadeResponse>): Promise<AcessibilidadeResponse> {
  return apiFetch<AcessibilidadeResponse>('PUT', '/api/v1/account/settings/accessibility', data);
}

export async function restoreAccessibility(): Promise<AcessibilidadeResponse> {
  return apiFetch<AcessibilidadeResponse>('POST', '/api/v1/account/settings/accessibility/restore');
}

// ─── Notificações ───
export interface NotificacoesResponse {
  creditoPontos: boolean;
  creditoXP: boolean;
  rastreioEnvio: boolean;
  novosDesafios: boolean;
  novasCampanhas: boolean;
  distintivosConquistados: boolean;
  lembretesMissoes: boolean;
  atualizacoesRanking: boolean;
}

export async function getNotifications(): Promise<NotificacoesResponse> {
  return apiFetch<NotificacoesResponse>('GET', '/api/v1/account/settings/notifications');
}

export async function updateNotifications(data: Partial<NotificacoesResponse>): Promise<NotificacoesResponse> {
  return apiFetch<NotificacoesResponse>('PUT', '/api/v1/account/settings/notifications', data);
}

// ─── Canais de Contato ───
export interface ContactChannelsResponse {
  email: boolean;
  sms: boolean;
  push: boolean;
  whatsapp: boolean;
}

export async function getContactChannels(): Promise<ContactChannelsResponse> {
  return apiFetch<ContactChannelsResponse>('GET', '/api/v1/account/settings/contactChannels');
}

export async function updateContactChannels(data: Partial<ContactChannelsResponse>): Promise<ContactChannelsResponse> {
  return apiFetch<ContactChannelsResponse>('PUT', '/api/v1/account/settings/contactChannels', data);
}

export async function associateWhatsApp(telefone: string): Promise<{ telefone: string; codigoEnviado: boolean; validadeMinutos: number }> {
  return apiFetch<{ telefone: string; codigoEnviado: boolean; validadeMinutos: number }>(
    'POST',
    '/api/v1/account/settings/contactChannels/whatsapp',
    { telefone }
  );
}

export async function validateWhatsApp(codigo: string): Promise<{ telefone: string; verificado: boolean }> {
  return apiFetch<{ telefone: string; verificado: boolean }>(
    'POST',
    '/api/v1/account/settings/contactChannels/whatsapp/validate',
    { codigo }
  );
}

// ─── Token Config ───
export interface TokenConfigResponse {
  email: boolean;
  sms: boolean;
}

export async function getTokenConfig(): Promise<TokenConfigResponse> {
  return apiFetch<TokenConfigResponse>('GET', '/api/v1/account/settings/token');
}

export async function updateTokenConfig(data: TokenConfigResponse): Promise<TokenConfigResponse> {
  return apiFetch<TokenConfigResponse>('PUT', '/api/v1/account/settings/token', data);
}

// ─── 2FA ───
export interface TwoFactorStatusResponse {
  ativo: boolean;
  metodo: 'email' | 'sms' | 'app';
  metodosDisponiveis: Array<'email' | 'sms' | 'app'>;
}

export async function get2FAStatus(): Promise<TwoFactorStatusResponse> {
  return apiFetch<TwoFactorStatusResponse>('GET', '/api/v1/account/settings/2fa');
}

export async function enable2FA(metodo: 'email' | 'sms' | 'app'): Promise<{
  metodo: string;
  destinoMascarado: string;
  codigoEnviado: boolean;
  validadeMinutos: number;
}> {
  return apiFetch<{
    metodo: string;
    destinoMascarado: string;
    codigoEnviado: boolean;
    validadeMinutos: number;
  }>('POST', '/api/v1/account/settings/2fa/enable', { metodo });
}

export async function confirmEnable2FA(codigo: string): Promise<{
  ativo: boolean;
  metodo: string;
  codigosBackup: string[];
}> {
  return apiFetch<{
    ativo: boolean;
    metodo: string;
    codigosBackup: string[];
  }>('POST', '/api/v1/account/settings/2fa/enable/confirm', { codigo });
}

export async function change2FAMethod(novoMetodo: 'email' | 'sms' | 'app', codigoAtual: string): Promise<{
  metodo: string;
  qrCodeUrl: string;
  chaveSecreta: string;
}> {
  return apiFetch<{
    metodo: string;
    qrCodeUrl: string;
    chaveSecreta: string;
  }>('PUT', '/api/v1/account/settings/2fa/method', { novoMetodo, codigoAtual });
}

export async function disable2FA(codigo: string): Promise<{ ativo: boolean }> {
  return apiFetch<{ ativo: boolean }>('POST', '/api/v1/account/settings/2fa/disable', { codigo });
}

// ─── Privacidade ───
export interface PrivacyResponse {
  perfilVisivel: 'todos' | 'amigos' | 'ninguem';
  rankingVisivel: boolean;
  feedVisivel: boolean;
  conquistasVisiveis: boolean;
  pontosVisiveis: boolean;
}

export async function getPrivacy(): Promise<PrivacyResponse> {
  return apiFetch<PrivacyResponse>('GET', '/api/v1/account/settings/privacy');
}

export async function updatePrivacy(data: Partial<PrivacyResponse>): Promise<PrivacyResponse> {
  return apiFetch<PrivacyResponse>('PUT', '/api/v1/account/settings/privacy', data);
}

// ─── Sessões ───
export interface SessionItem {
  id: string;
  dispositivo: string;
  navegador: string;
  sistema: string;
  ip: string;
  localizacao: string;
  ultimoAcesso: string;
  atual: boolean;
}

export async function getSessions(): Promise<{ sessoes: SessionItem[]; total: number }> {
  return apiFetch<{ sessoes: SessionItem[]; total: number }>('GET', '/api/v1/account/settings/sessions');
}

export async function terminateSession(idSession: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('DELETE', `/api/v1/account/settings/sessions/${idSession}`);
}

export async function terminateOtherSessions(): Promise<{ sessoesEncerradas: number; sessaoAtualMantida: string }> {
  return apiFetch<{ sessoesEncerradas: number; sessaoAtualMantida: string }>(
    'DELETE',
    '/api/v1/account/settings/sessions/all'
  );
}

// ─── Histórico de Acessos ───
export interface AccessHistoryItem {
  id: string;
  data: string;
  hora: string;
  dispositivo: string;
  ip: string;
  localizacao: string;
  status: 'sucesso' | 'falha';
  motivoFalha?: string;
}

export interface AccessHistoryResponse {
  acessos: AccessHistoryItem[];
  paginacao: {
    pagina: number;
    limite: number;
    totalPaginas: number;
    totalItens: number;
  };
}

export async function getAccessHistory(pagina = 1, limite = 10): Promise<AccessHistoryResponse> {
  return apiFetch<AccessHistoryResponse>(
    'GET',
    `/api/v1/account/settings/access-history?pagina=${pagina}&limite=${limite}`
  );
}

// ─── Encerramento de Conta ───
export interface AccountClosureResponse {
  protocolo: string;
  status: string;
  prazoDias: number;
  dataLimiteReversao: string;
  observacao: string;
}

export async function closeAccount(senha: string, confirmacao: string, motivo?: string): Promise<AccountClosureResponse> {
  return apiFetch<AccountClosureResponse>('POST', '/api/v1/account/settings/account-closure', {
    senha,
    confirmacao,
    motivo,
  });
}

export async function revertAccountClosure(): Promise<{ protocolo: string; status: string }> {
  return apiFetch<{ protocolo: string; status: string }>(
    'POST',
    '/api/v1/account/settings/account-closure/reverter'
  );
}

// ─── Todas as Configurações (overview) ───
export interface AllSettingsResponse {
  idioma: string;
  acessibilidade: AcessibilidadeResponse;
  notificacoes: NotificacoesResponse;
  preferenciasContato: ContactChannelsResponse;
  tokenConfig: TokenConfigResponse;
  autenticacao2FA: TwoFactorStatusResponse;
  privacidade: PrivacyResponse;
}

export async function getAllSettings(): Promise<AllSettingsResponse> {
  return apiFetch<AllSettingsResponse>('GET', '/api/v1/account/settings');
}